import { AccessTime, ArrowForward, MenuBook, OpenInNew, Sensors, ShieldOutlined, WarningAmber } from '@mui/icons-material';

import VnModalBody from '@/modals/components/VnModalBody';
import { useModalContext } from '@/modals/VNModalProvider';
import useAppUtils from '@/utils/appUtils';
import clientUtils from '@/utils/clientUtils';

import { DEFAULT_RESOLUTION, PROTOCOL_SRT } from './inputSourceFormConfig';

const SUPPORT_HELP_URL = 'https://help.bitcentral.com/help/viewnexa-articles';

const ENCODER_OPTIONS = [
  {
    name: 'Videon',
    subtitle: 'Hardware · broadcast-grade appliance',
    setupUrl: SUPPORT_HELP_URL,
  },
  {
    name: 'Haivision',
    subtitle: 'Hardware · enterprise streaming',
    setupUrl: SUPPORT_HELP_URL,
  },
  {
    name: 'OBS Studio',
    subtitle: 'Free · recommended for beginners',
    setupUrl: SUPPORT_HELP_URL,
  },
  {
    name: 'vMix',
    subtitle: 'Windows · multi-camera production',
  },
  {
    name: 'Wirecast',
    subtitle: 'Mac & Windows · broadcast-grade',
  },
  {
    name: 'Any Other SRT Encoder',
    subtitle: 'Use the SRT ingest URL with any compatible encoder',
  },
] as const;

const COMMON_ISSUES = [
  {
    title: 'No signal received',
    description: 'Double-check the Ingest URL and Stream Key, then click Start Streaming again.',
  },
  {
    title: 'Video but no audio',
    description: "In your encoder's Audio settings, confirm the right microphone is selected and unmuted.",
  },
  {
    title: 'Choppy or buffering preview',
    description: 'Lower your bitrate by 30%, or switch to a wired connection.',
  },
  {
    title: 'Dropped frames in encoder',
    description: 'Your upload bandwidth is too low. Test at fast.com — you need ~1.5× your bitrate.',
  },
] as const;

type Props = {
  serverUrl?: string;
  streamKey?: string;
  protocol?: string;
  resolution?: string;
  onCredentialsCopied?: () => void;
  onScheduleBroadcast?: () => void;
};

const InputSourceHelpModal = ({
  serverUrl = '',
  streamKey = '',
  protocol = PROTOCOL_SRT,
  resolution = DEFAULT_RESOLUTION,
  onCredentialsCopied,
  onScheduleBroadcast,
}: Props) => {
  const { onClose } = useModalContext();
  const { alert } = useAppUtils();
  const hasCredentials = Boolean(serverUrl || streamKey);
  const protocolLabel = protocol?.trim() || PROTOCOL_SRT;
  const resolutionLabel = resolution?.trim() || DEFAULT_RESOLUTION;

  const copyValue = (value: string, label: string) => {
    if (!value) {
      alert(`${label} is not available yet.`, 'warning');
      return;
    }
    clientUtils.copyToClipboard(value);
    alert(`${label} copied.`, 'success');
    onCredentialsCopied?.();
  };

  const handleScheduleBroadcast = () => {
    onClose();
    onScheduleBroadcast?.();
  };

  return (
    <VnModalBody>
      <div className="input-source-help-modal">
        <p className="input-source-help-modal__intro">
          Step-by-step guide tailored to this input source. Your Ingest URL and Stream Key are pre-filled below.
        </p>

        <section className="input-source-help-modal__section">
          <h3 className="input-source-help-modal__step-heading">
            <span className="input-source-help-modal__step-number">1</span>
            Pick your encoder
          </h3>
          <p className="input-source-help-modal__body-text">
            An encoder is the software or device that sends your video to ViewNexa. Don&apos;t have one? We recommend the Videon
            encoder.{' '}
           Reach out to Support for more information
            .
          </p>
          {/* <div className="input-source-help-modal__encoder-grid">
            {ENCODER_OPTIONS.map((encoder) => (
              <div key={encoder.name} className="input-source-help-modal__encoder-card">
                <p className="input-source-help-modal__encoder-name">{encoder.name}</p>
                <p className="input-source-help-modal__encoder-subtitle">{encoder.subtitle}</p>
                {'setupUrl' in encoder && encoder.setupUrl ? (
                  <a
                    className="input-source-help-modal__encoder-setup"
                    href={encoder.setupUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <MenuBook fontSize="inherit" />
                    See set up
                    <OpenInNew fontSize="inherit" className="input-source-help-modal__encoder-setup-icon" />
                  </a>
                ) : null}
              </div>
            ))}
          </div> */}
        </section>

        <section className="input-source-help-modal__section">
          <h3 className="input-source-help-modal__step-heading">
            <span className="input-source-help-modal__step-number">2</span>
            Paste your connection details
          </h3>
          <p className="input-source-help-modal__body-text">
            After creating your new input source, within your encoder&apos;s <strong>Stream</strong> or <strong>Output</strong>{' '}
            settings, choose <span className="input-source-help-modal__protocol-badge">{protocolLabel}</span> as the service and
            paste the values below.
          </p>

          {!hasCredentials ? (
            <p className="input-source-help-modal__credentials-note">
              Create or open an input source to see your Ingest URL and Stream Key pre-filled here.
            </p>
          ) : null}

          <div className="input-source-help-modal__credentials-box">
            <div className="input-source-help-modal__credential-row">
              <span className="input-source-help-modal__field-label">Ingest URL</span>
              <div className="input-source-help-modal__value-row">
                <span className="input-source-help-modal__value">{serverUrl || '—'}</span>
                <span title="Copy Ingest URL" className="copy-button icon">
                  <i aria-hidden="true" className="fa fa-clone" onClick={() => copyValue(serverUrl, 'Ingest URL')} />
                </span>
              </div>
            </div>
            <div className="input-source-help-modal__credential-row">
              <div className="input-source-help-modal__stream-key-label-row">
                <span className="input-source-help-modal__field-label">Stream Key</span>
                <span className="input-source-help-modal__stream-key-hint">
                  <ShieldOutlined fontSize="inherit" />
                  Treat like a password
                </span>
              </div>
              <div className="input-source-help-modal__value-row">
                <span className="input-source-help-modal__value">{streamKey || '—'}</span>
                <span title="Copy Stream Key" className="copy-button icon">
                  <i aria-hidden="true" className="fa fa-clone" onClick={() => copyValue(streamKey, 'Stream Key')} />
                </span>
              </div>
            </div>
          </div>
        </section>


        <section className="input-source-help-modal__section">
          <h3 className="input-source-help-modal__step-heading">
            <span className="input-source-help-modal__step-number">3</span>
            Press &quot;Start Streaming&quot; in your encoder
          </h3>
          <p className="input-source-help-modal__body-text">
            Within ~90 seconds the source status will turn <span className="status-ready">Connected</span> and your video will
            appear in the preview player on this page. That&apos;s it — you&apos;re live to ViewNexa.
          </p>
          <p className="input-source-help-modal__note">
            <AccessTime fontSize="inherit" />
            Preview runs ~3 seconds behind your encoder.
          </p>
        </section>

        <section className="input-source-help-modal__section">
          <h3 className="input-source-help-modal__step-heading">
            <span className="input-source-help-modal__step-number">4</span>
            Validate the stream in the preview player
          </h3>
          <p className="input-source-help-modal__body-text">
            Use the preview player on this page to confirm your stream looks and sounds right. Press play and check:
          </p>
          <ul className="input-source-help-modal__bullet-list">
            <li>Video is visible and oriented correctly</li>
            <li>Audio plays in sync with the video</li>
            <li>No major stutters, freezes, or artifacts</li>
          </ul>
          <p className="input-source-help-modal__body-text">
            If everything looks good, your Input Source is ready to use in a broadcast or channel.
          </p>
        </section>

        <hr className="input-source-help-modal__divider" />

        <section className="input-source-help-modal__section">
          <h3 className="input-source-help-modal__issues-heading">
            <WarningAmber fontSize="small" className="input-source-help-modal__issues-icon" />
            Common issues
          </h3>
          <div className="input-source-help-modal__issues">
            {COMMON_ISSUES.map((issue) => (
              <div key={issue.title} className="input-source-help-modal__issue-card">
                <p className="input-source-help-modal__issue-title">{issue.title}</p>
                <p className="input-source-help-modal__issue-description">{issue.description}</p>
              </div>
            ))}
          </div>
        </section>

        {onScheduleBroadcast ? (
          <>
            <hr className="input-source-help-modal__divider" />
            <section className="input-source-help-modal__section">
              <h3 className="input-source-help-modal__whats-next-heading">
                <Sensors fontSize="small" className="input-source-help-modal__whats-next-icon" />
                What&apos;s next
              </h3>
              <button type="button" className="input-source-help-modal__whats-next-btn" onClick={handleScheduleBroadcast}>
                <span>Schedule your first broadcast</span>
                <ArrowForward fontSize="small" />
              </button>
            </section>
          </>
        ) : null}
      </div>
    </VnModalBody>
  );
};

export default InputSourceHelpModal;
